<script setup>
import { ref, computed } from "vue";

// ── Persistent CRM Guest Profile (exists across all stays / reservations) ────
const profile = ref({
  guestProfileId: "GP-00043821",
  loyaltyNumber: "OD-884421",      // OUTRIGGER DISCOVERY loyalty number
  loyaltyProgram: "OUTRIGGER DISCOVERY",
  loyaltyTier: "Gold",             // Tiers: Silver · Gold · Platinum · Titanium
  discoveryDollars: 142.50,        // D$ balance redeemable on rooms, dining, experiences
  totalStays: 3,
  nightsThisYear: 4,               // Platinum requires 10 nights/year
  firstName: "Emily",
  lastName: "Johnson",
  dateOfBirth: "1990-07-22",
  nationality: "American",
  idType: "Passport",
  idNumber: "456712398",
  email: "emily.johnson@email.com",
  phone: "+1 (808) 555-5678",
  address: "88 Maple Avenue, Portland, Oregon, USA 97201",
  preferences: "Ocean-view room · Upper floors · Non-smoking",
  createdDate: "2024-08-05",
  lastUpdated: "2026-04-28",
});

const guest = ref({
  // Mirrors API: reservations.TH862232 + property id:2
  reservationId: "TH862232",
  propertyId: 2,
  status: "Confirmed",
  property: "Outrigger Waikiki Beachcomber Hotel",
  location: "Waikiki, Honolulu, Hawaii, USA",
  room: "Deluxe Suite",
  checkIn: "2026-05-15",
  checkOut: "2026-05-17",
  nights: 2,
  ratePerNight: 202,
  guests: 2,
  firstName: "Emily",
  lastName: "Johnson",
  nationality: "American",
  dateOfBirth: "1990-07-22",
  email: "emily.johnson@email.com",
  phone: "+1 (808) 555-5678",
  address: "88 Maple Avenue, Portland, Oregon, USA 97201",
  paymentStatus: "Partial",
  totalCharges: 887,   // 2 nights × $202 = $404 room + extras below
  amountPaid: 404,     // room deposit paid
  outstandingBalance: 482,
  pendingItems: [
    { item: "Spa – Hot Stone Massage (May 16)",  amount: 85  },
    { item: "Romantic Dinner for 2 (May 15)",    amount: 148 },
    { item: "Mini-bar Consumption",              amount: 32  },
    { item: "Waikiki City Tour (May 16)",        amount: 79  },
    { item: "Airport Transfer (Departure)",      amount: 45  },
    { item: "Laundry Service",                   amount: 28  },
    { item: "Snorkeling Excursion (May 16)",     amount: 65  },
  ],
  specialRequests: [],
});

const remarks = ref([
  { id: 1, date: "2026-05-04", type: "Request",   agent: "K. Akana",   note: "Guest called ahead — requested early check-in on May 15 if possible. Noted in reservation, subject to availability." },
  { id: 2, date: "2026-04-28", type: "Request",   agent: "L. Kahale",  note: "Ocean-view room preference added to reservation. Guest also asked about late checkout on May 17 — advised to call closer to date." },
  { id: 3, date: "2025-11-20", type: "Upgrade",   agent: "System",     note: "Previous stay (Outrigger Kauai Beach Resort & Spa): Complimentary upgrade from Deluxe to Ocean-view room — OUTRIGGER DISCOVERY Gold recognition." },
  { id: 4, date: "2025-11-19", type: "Complaint", agent: "M. Kealoha", note: "Previous stay (Outrigger Kauai): Reported slow hot water in room 408. Resolved in 30 min. $25 D$ credit issued as goodwill." },
  { id: 5, date: "2025-11-18", type: "Discount",  agent: "K. Akana",   note: "Previous stay (Outrigger Kauai): Gold tier 5% D$ reward applied. Guest redeemed $30 D$ toward spa treatment." },
  { id: 6, date: "2024-08-10", type: "Note",      agent: "L. Kahale",  note: "First stay (Outrigger Waikiki Beach Resort): Guest prefers ocean-view room, upper floors. Enjoyed Duke's Waikiki — noted for future stays." },
]);

// const timeline = ref([
//   { id: 1, date: "2026-05-04", type: "Note",      note: "Pre-arrival: early check-in May 15 requested · noted subject to availability" },
//   { id: 2, date: "2026-04-28", type: "Request",   note: "Special request added: ocean-view room · late checkout May 17 inquiry noted" },
//   { id: 3, date: "2026-04-10", type: "Booking",   note: "Reservation TH862232 created · Deluxe Suite · May 15–17 · airport transfer added" },
//   { id: 4, date: "2025-11-20", type: "Upgrade",   note: "Outrigger Kauai Beach Resort: Complimentary upgrade to Ocean-view · DISCOVERY Gold recognition" },
//   { id: 5, date: "2025-11-19", type: "Complaint", note: "Outrigger Kauai: Slow hot water room 408 · resolved 30 min · $25 D$ credit issued" },
//   { id: 6, date: "2025-11-15", type: "Note",      note: "Outrigger Kauai: Kama'aina spa discount 10% confirmed and applied to folio" },
//   { id: 7, date: "2024-08-10", type: "Check-out", note: "Outrigger Waikiki Beach Resort: Checked out · 2 nights · Gold tier D$ reward posted" },
//   { id: 8, date: "2024-08-05", type: "Booking",   note: "First booking · Outrigger Waikiki Beach Resort Aug 10–12 · enrolled in OUTRIGGER DISCOVERY" },
// ]);

const upsells = ref([
  { id: 1, icon: "💆", title: "Spa Treatment",             description: "60-min massage or facial at Waikiki Spa",          price: "$85",  offered: false },
  { id: 2, icon: "🍽️", title: "Dinner at Duke's Waikiki",  description: "Oceanfront table for 2 at Duke's Waikiki",         price: "$148", offered: false },
  { id: 3, icon: "🎵", title: "Blue Note Hawaii",          description: "Live jazz show tickets — iconic Waikiki venue",     price: "$95",  offered: false },
  { id: 4, icon: "🏄", title: "Surf Lesson",               description: "Beginner surf lesson with certified instructor",    price: "$75",  offered: false },
  { id: 5, icon: "🤿", title: "Hanauma Bay Snorkel",       description: "Guided reef snorkel tour with equipment",           price: "$65",  offered: true  },
  { id: 6, icon: "🌺", title: "Lei Making Workshop",       description: "Traditional Hawaiian lei making cultural class",    price: "$45",  offered: false },
  { id: 7, icon: "🚣", title: "Outrigger Canoe Ride",      description: "Guided outrigger canoe experience off Waikiki",     price: "$55",  offered: false },
]);

const showIdNumber  = ref(false);
// const idTypes       = ["Driver's License", "Passport", "National ID", "Military ID", "Other"];
const newRemarkNote = ref("");
const newRemarkType = ref("Request");
const remarkTypes   = ["Request", "Complaint", "Upgrade", "Discount", "Note"];

const fmt = (v) => "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2 });

const balancePct = computed(() =>
  Math.round((guest.value.amountPaid / guest.value.totalCharges) * 100)
);

const remarkTypeColor = (type) => ({
  Request:   "#1d6fa5",
  Complaint: "#b91c1c",
  Upgrade:   "#065f46",
  Discount:  "#92400e",
  Note:      "#374151",
}[type] || "#374151");

// const tlTypeColor = (type) => ({
//   "Booking":   "#1d6fa5",
//   "Check-out": "#374151",
//   "Upgrade":   "#065f46",
//   "Complaint": "#b91c1c",
//   "Request":   "#1d6fa5",
//   "Note":      "#374151",
// }[type] || "#374151");

function saveRemark() {
  if (!newRemarkNote.value.trim()) return;
  remarks.value.unshift({
    id: Date.now(),
    date: new Date().toISOString().slice(0, 10),
    type: newRemarkType.value,
    agent: "Current Agent",
    note: newRemarkNote.value.trim(),
  });
  newRemarkNote.value = "";
}
</script>

<template>
  <div class="gi-page">
    <div class="gi-card">

      <!-- ── Card header: title left, screen code right ─────────────────── -->
      <div class="gi-topbar">
        <h2 class="gi-screen-id">Guest Information</h2>
        <div class="gi-topbar-right">
          <button class="gi-goto-btn">Go to PMS</button>
          <span class="gi-screen-code">G01</span>
        </div>
      </div>

      <!-- ── Subheader strip ─────────────────────────────────────────────── -->
      <div class="gi-subheader">
      <div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Reservation:</span>
          <span class="gi-sub-val">{{ guest.reservationId }}</span>
        </div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Name:</span>
          <span class="gi-sub-val gi-sub-val--name">{{ guest.firstName }} {{ guest.lastName }}</span>
        </div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Email:</span>
          <span class="gi-sub-val">{{ guest.email }}</span>
        </div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Phone:</span>
          <span class="gi-sub-val">{{ guest.phone }}</span>
        </div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Address:</span>
          <span class="gi-sub-val">{{ guest.address }}</span>
        </div>
      </div>
      <div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Status:</span>
          <span class="gi-sub-val gi-sub-val--status">{{ guest.status }}</span>
        </div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Property:</span>
          <span class="gi-sub-val">{{ guest.property }}</span>
        </div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Room:</span>
          <span class="gi-sub-val">{{ guest.room }}</span>
        </div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Check-in:</span>
          <span class="gi-sub-val">{{ guest.checkIn }}</span>
        </div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Check-out:</span>
          <span class="gi-sub-val">{{ guest.checkOut }} &nbsp;({{ guest.nights }} nights · {{ guest.guests }} guests)</span>
        </div>
      </div>
      <div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Payment:</span>
          <span class="gi-sub-val" style="font-weight:700;">{{ guest.paymentStatus }}</span>
        </div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Total:</span>
          <span class="gi-sub-val">{{ fmt(guest.totalCharges) }}</span>
        </div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Paid:</span>
          <span class="gi-sub-val" style="color:#16a34a; font-weight:600;">{{ fmt(guest.amountPaid) }}</span>
        </div>
        <div class="gi-sub-row">
          <span class="gi-sub-lbl">Outstanding:</span>
          <span class="gi-sub-val" style="color:#dc2626; font-weight:700;">{{ fmt(guest.outstandingBalance) }}</span>
        </div>
        <div class="gi-progress-wrap" style="margin-top:6px;">
          <div class="gi-progress-bar">
            <div class="gi-progress-fill" :style="{ width: balancePct + '%' }"></div>
          </div>
          <span class="gi-progress-lbl">{{ balancePct }}% paid</span>
        </div>
      </div>
    </div>

    <!-- ── 3-column body ──────────────────────────────────────────────────── -->
    <div class="gi-centerdiv">

      <!-- ═══ COL 1: Identity + Payment ════════════════════════════════════ -->
      <div class="gi-col">

        <!-- Search Reservation -->
        <h4>Search for</h4>
        <div class="gi-search-group">
          <input class="gi-search-input" type="text" placeholder="Itinerary, confirmation, or item number" />
          <input class="gi-search-input" type="text" placeholder="Email Address" />
        </div>
        <div style="margin: 8px 0 18px;">
          <button class="gi-submit-btn">Find</button>
          <button class="gi-cancel-btn">Cancel</button>
        </div>

        <!-- Guest Profile (CRM persistent record) -->
        <h4>Guest Identity</h4>

        <!-- Profile ID strip -->
        <div class="gi-profile-ids">
          <div class="gi-profile-id-item">
            <span class="gi-sub-lbl">Profile ID</span>
            <span class="gi-profile-id-val">{{ profile.guestProfileId }}</span>
          </div>
          <div class="gi-profile-id-item">
            <span class="gi-sub-lbl">Loyalty No.</span>
            <span class="gi-profile-id-val">{{ profile.loyaltyNumber }}</span>
          </div>
          <div class="gi-profile-id-item">
            <span class="gi-sub-lbl">Tier</span>
            <span class="gi-loyalty-badge" :class="'tier-' + profile.loyaltyTier.toLowerCase()">
              {{ profile.loyaltyTier }}
            </span>
          </div>
          <div class="gi-profile-id-item">
            <span class="gi-sub-lbl">Total Stays</span>
            <span class="gi-profile-id-val">{{ profile.totalStays }}</span>
          </div>
          <div class="gi-profile-id-item">
            <span class="gi-sub-lbl">Nights (YTD)</span>
            <span class="gi-profile-id-val">{{ profile.nightsThisYear }} / 10
              <span class="gi-nights-hint">→ Platinum</span>
            </span>
          </div>
        </div>

        <table class="gi-info-table" style="margin-top: 8px;">
          <tbody>
            <tr>
              <td class="gi-td-lbl">ID Type</td>
              <td>
                {{  profile.idType  }}
              </td>
            </tr>
            <tr>
              <td class="gi-td-lbl">ID Number</td>
              <td>
                {{ showIdNumber ? profile.idNumber : "••••••••••" }}
                <button class="gi-pill-btn" @click="showIdNumber = !showIdNumber">
                  {{ showIdNumber ? "Hide" : "Show" }}
                </button>
              </td>
            </tr>
            <tr><td class="gi-td-lbl">Nationality</td><td>{{ profile.nationality }}</td></tr>
            <tr><td class="gi-td-lbl">Date of Birth</td><td>{{ profile.dateOfBirth }}</td></tr>
            <tr><td class="gi-td-lbl">Preferences</td><td>{{ profile.preferences }}</td></tr>
            <tr><td class="gi-td-lbl">Profile Since</td><td>{{ profile.createdDate }}</td></tr>
            <tr><td class="gi-td-lbl">Last Updated</td><td>{{ profile.lastUpdated }}</td></tr>
          </tbody>
        </table>

        <!-- Special Requests -->
        <h4 style="margin-top: 20px;">Special Requests</h4>
        <textarea class="gi-textarea" rows="3" readonly
          :value="guest.specialRequests.join('\n')"></textarea>

        <!-- Pending Items -->
        <h4 style="margin-top: 20px;">Pending Items to Settle</h4>
        <table class="gi-data-table">
          <thead>
            <tr><th>Item</th><th>Amount</th></tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in guest.pendingItems" :key="i">
              <td>{{ item.item }}</td>
              <td style="text-align:right; white-space:nowrap;">{{ fmt(item.amount) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Total Outstanding</strong></td>
              <td style="text-align:right; white-space:nowrap;">
                <strong>{{ fmt(guest.pendingItems.reduce((s,i) => s + i.amount, 0)) }}</strong>
              </td>
            </tr>
          </tfoot>
        </table>

      </div>

      <!-- ═══ COL 2: Remarks + Upsell ══════════════════════════════════════ -->
      <div class="gi-col">

        <!-- Remarks -->
        <h4>Remarks &amp; Guest History</h4>
        <div style="display:flex; gap:8px; margin-bottom:10px; align-items:flex-start;">
          <div class="gi-remark-type-wrap">
            <select class="gi-remark-type-select" v-model="newRemarkType">
              <option v-for="t in remarkTypes" :key="t">{{ t }}</option>
            </select>
            <span class="gi-remark-type-chevron">▾</span>
          </div>
          <textarea class="gi-textarea" v-model="newRemarkNote"
            placeholder="Add a remark..." rows="2"
            style="flex:1; margin:0;"></textarea>
        </div>
        <div style="margin-bottom:14px;">
          <button class="gi-submit-btn" @click="saveRemark">Save</button>
          <button class="gi-cancel-btn" @click="newRemarkNote=''">Cancel</button>
        </div>

        <table class="gi-data-table">
          <thead>
            <tr><th>Type</th><th>Date</th><th>Agent</th><th>Note</th></tr>
          </thead>
          <tbody>
            <tr v-for="r in remarks" :key="r.id">
              <td style="white-space:nowrap;">
                <span class="gi-type-dot" :style="{ background: remarkTypeColor(r.type) }"></span>
                {{ r.type }}
              </td>
              <td style="white-space:nowrap; color:#555;">{{ r.date }}</td>
              <td style="white-space:nowrap; color:#555;">{{ r.agent }}</td>
              <td>{{ r.note }}</td>
            </tr>
          </tbody>
        </table>

      </div>

      <!-- ═══ COL 3: Activity Timeline + Upsell ════════════════════════════ -->
      <div class="gi-col">

        <!-- <h4>Guest History
          <span class="gi-source-note">Source: PMS</span>
        </h4>

        <div class="gi-tl-list">
          <div v-for="entry in timeline" :key="entry.id" class="gi-tl-entry">
            <span class="gi-type-dot" :style="{ background: tlTypeColor(entry.type) }"></span>
            <span class="gi-tl-type" :style="{ color: tlTypeColor(entry.type) }">{{ entry.type }}</span>
            <span class="gi-tl-time">{{ entry.date }}</span>
            <span class="gi-tl-note">{{ entry.note }}</span>
          </div>
        </div> -->

        <!-- Personalized Offers -->
        <h4 style="margin-top: 24px;">Personalized Offers</h4>
        <p class="gi-hint">Click a row to mark as offered to the guest.</p>
        <table class="gi-data-table">
          <thead>
            <tr><th></th><th>Service</th><th>Price</th><th>Status</th></tr>
          </thead>
          <tbody>
            <tr v-for="u in upsells" :key="u.id"
              class="gi-upsell-row" :class="{ offered: u.offered }"
              @click="u.offered = !u.offered">
              <td>{{ u.icon }}</td>
              <td>{{ u.title }}</td>
              <td style="white-space:nowrap;">{{ u.price }}</td>
              <td>
                <span v-if="u.offered" class="gi-offered-badge">Offered ✓</span>
                <span v-else class="gi-pending-badge">—</span>
              </td>
            </tr>
          </tbody>
        </table>

      </div>

    </div>
    </div><!-- /gi-card -->
  </div>
</template>

<style scoped>
/* ── Page shell — grey outer background ──────────────────────────────────── */
.gi-page {
  font-family: "Open Sans", "Segoe UI", sans-serif;
  font-size: 15px;
  background: #e8e8e8;
  min-height: 100vh;
  padding: 20px;
  color: #222;
  box-sizing: border-box;
}

/* ── Card — single white rounded container (like M01/T01) ────────────────── */
.gi-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.12);
  overflow: hidden;
}

/* ── Card header: title left, G01 right ─────────────────────────────────── */
.gi-topbar {
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e8e8e8;
}
.gi-screen-id    { font-size: 18px; font-weight: 600; color: #222; margin: 0; }
.gi-screen-code  { font-size: 18px; font-weight: 600; color: #888; }
.gi-topbar-right { display: flex; align-items: center; gap: 14px; }
.gi-goto-btn {
  background: #0e5071;
  color: #fff;
  border: none;
  border-radius: 50px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.02em;
}
.gi-goto-btn:hover { background: #0c4160; }

/* ── Subheader strip ─────────────────────────────────────────────────────── */
.gi-subheader {
  border-bottom: 1px solid #e8e8e8;
  padding: 10px 20px;
  display: flex;
  gap: 60px;
}
.gi-sub-row  { display: flex; gap: 6px; padding: 1px 0; font-size: 14px; }
.gi-sub-lbl  { color: #555; min-width: 80px; }
.gi-sub-val  { color: #222; }
.gi-sub-val--name   { font-weight: 600; }
.gi-sub-val--status { font-weight: 700; color: #1d6fa5; }

/* ── 3-column centerdiv (mirrors M01 containerdiv layout) ────────────────── */
.gi-centerdiv {
  display: flex;
  gap: 0;
  padding: 0;
  align-items: flex-start;
}
.gi-col {
  flex: 1;
  padding: 16px 20px;
  min-width: 0;
  border-right: 1px solid #e8e8e8;
}
.gi-col:last-child { border-right: none; }
.gi-col h4 {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 10px;
  color: #222;
}

/* ── Plain key-value table (identity, payment) ────────────────────────────── */
/* ── Guest Profile ID strip ──────────────────────────────────────────────── */
.gi-profile-ids {
  display: flex;
  gap: 0;
  background: #f5f7f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}
.gi-profile-id-item {
  flex: 1;
  padding: 7px 10px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.gi-profile-id-item:last-child { border-right: none; }
.gi-profile-id-val {
  font-size: 13px;
  font-weight: 600;
  color: #0e5071;
  font-family: monospace;
}
.gi-loyalty-badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  margin-top: 1px;
}
.tier-gold     { background: #fef3c7; color: #92400e; }
.tier-silver   { background: #f1f5f9; color: #475569; }
.tier-platinum { background: #ede9fe; color: #5b21b6; }
.tier-titanium { background: #1e293b; color: #e2e8f0; }
.tier-standard { background: #f0f0f0; color: #555;    }
.gi-nights-hint { font-size: 10px; color: #888; font-family: sans-serif; margin-left: 2px; }

/* ── Search ───────────────────────────────────────────────────────────────── */
.gi-search-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 0; }
.gi-search-input {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 5px 8px;
  font-size: 14px;
  font-family: inherit;
  box-sizing: border-box;
}
.gi-search-input:focus { outline: none; border-color: #0e5071; }

.gi-info-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.gi-info-table td { padding: 4px 6px; vertical-align: top; }
.gi-td-lbl { color: #555; width: 110px; white-space: nowrap; }

/* ── Data table (teal header — matches M01 member-info-table) ─────────────── */
.gi-data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.gi-data-table thead tr { background: #0e5071; }
.gi-data-table th {
  padding: 7px 10px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #fff !important;
  white-space: nowrap;
}
.gi-data-table tbody tr:nth-child(odd)  { background: #fff; }
.gi-data-table tbody tr:nth-child(even) { background: #f5f7f9; }
.gi-data-table tbody tr { border-bottom: 1px solid #e8e8e8; }
.gi-data-table tbody tr:last-child { border-bottom: none; }
.gi-data-table td { padding: 6px 10px; vertical-align: top; color: #333; }
.gi-data-table tfoot td { background: #fff; border-top: 2px solid #ddd; padding-top: 8px; }

/* ── Progress bar ─────────────────────────────────────────────────────────── */
.gi-progress-wrap { display: flex; align-items: center; gap: 8px; margin: 8px 0 0; }
.gi-progress-bar  { flex: 1; height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
.gi-progress-fill { height: 100%; background: #16a34a; border-radius: 4px; }
.gi-progress-lbl  { font-size: 12px; color: #555; white-space: nowrap; }

/* ── Textarea ─────────────────────────────────────────────────────────────── */
.gi-textarea {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 6px 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  color: #222;
  box-sizing: border-box;
}

/* ── Remark type styled dropdown ─────────────────────────────────────────── */
.gi-remark-type-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.gi-remark-type-select {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid #b0b0b0;
  border-radius: 4px;
  padding: 5px 28px 5px 10px;
  font-size: 13px;
  font-family: inherit;
  font-weight: 600;
  background: #fff;
  color: #454a4d;
  cursor: pointer;
  min-width: 100px;
}
.gi-remark-type-select:focus { outline: none; border-color: #0e5071; }
.gi-remark-type-chevron {
  position: absolute;
  right: 8px;
  pointer-events: none;
  font-size: 11px;
  color: #888;
}

/* ── Select ───────────────────────────────────────────────────────────────── */
.gi-select {
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 5px 8px;
  font-size: 13px;
  font-family: inherit;
  background: #fff;
}
.gi-select--inline {
  padding: 2px 6px;
  font-size: 13px;
}

/* ── Buttons (grey pill style matching M01) ───────────────────────────────── */
.gi-submit-btn, .gi-cancel-btn, .gi-pill-btn {
  border: none;
  border-radius: 20px;
  padding: 5px 18px;
  font-size: 13px;
  cursor: pointer;
  margin-right: 6px;
}
.gi-submit-btn { background: #0e5071; color: #fff; }
.gi-submit-btn:hover { background: #0c4160; }
.gi-cancel-btn { background: #b0b0b0; color: #fff; }
.gi-cancel-btn:hover { background: #999; }
.gi-pill-btn   { background: #e0e0e0; color: #333; font-size: 11px; padding: 2px 10px; margin-left: 6px; }

/* ── Remark type dot ──────────────────────────────────────────────────────── */
.gi-type-dot {
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}

/* ── Hint text ────────────────────────────────────────────────────────────── */
.gi-hint { font-size: 12px; color: #888; margin: -6px 0 8px; }

/* ── Upsell row ───────────────────────────────────────────────────────────── */
.gi-upsell-row { cursor: pointer; }
.gi-upsell-row:hover { background: #f5f9fc; }
.gi-upsell-row.offered { background: #f0fdf4; }
.gi-offered-badge { background: #d1fae5; color: #065f46; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.gi-pending-badge  { color: #aaa; font-size: 13px; }

/* ── Source note inline with h4 ──────────────────────────────────────────── */
.gi-source-note {
  font-size: 11px;
  color: #888;
  font-weight: normal;
  margin-left: 8px;
}

/* ── Activity Timeline ────────────────────────────────────────────────────── */
.gi-tl-list  { display: flex; flex-direction: column; gap: 0; }
.gi-tl-entry {
  display: flex; align-items: baseline; gap: 6px;
  padding: 5px 8px; border-bottom: 1px solid #e8e8e8;
  font-size: 13px; flex-wrap: nowrap;
}
.gi-tl-entry:nth-child(even) { background: #f5f7f9; }
.gi-tl-entry:nth-child(odd)  { background: #fff; }
.gi-tl-entry:last-child { border-bottom: none; }
.gi-tl-type  { font-weight: 600; font-size: 12px; white-space: nowrap; flex-shrink: 0; }
.gi-tl-time  { font-size: 11px; color: #999; white-space: nowrap; flex-shrink: 0; }
.gi-tl-note  { color: #333; flex: 1; min-width: 0; }
</style>
